import React, { useEffect, useState } from "react";

import LineupDrop from "./LineupDrop";
import SubstitutionTable from "./SubstitutionTable";
import FtLineUp from "./FtLineUp";
import LineupBoxFootball from "./LineupBoxFootball";
import LineupRow from "./LineupRow";
import NoDataFound from "../NoDataFound";
import NextImage from "../NextImage";
import { loadStyles } from "../../../utils/cssLoader";
import { getImageUrl } from "../../../utils/generalLiveCenter";

// Default formation if none provided
const defaultFormation = "4-4-2";

function parseFormation(formation) {
  return formation ? formation.split("-").map(Number) : [];
}

const getShortName = (name) => {
  if (!name) return "";
  const words = name.split(" ");
  if (words.length === 1) return name.slice(0, 3).toUpperCase();
  return words.map((word) => word[0]).join("").toUpperCase().slice(0, 3);
};

const Linesup = ({ serverData, dataClient, loadCSS, title, sport = "football", data: propData }) => {
  const [clientData, setClientData] = useState(null);
  // Support both direct data prop and serverData/dataClient pattern
  const data = propData || (dataClient ? clientData : serverData);

  const baseURL =
    process.env.REACT_APP_BASE_URL ??
    "https://ipublisher-microservices.6lgx.com";

  const sanitizeKitColor = (kit, fallback) =>
    typeof kit === "string" && kit.length > 0
      ? `#${kit.replace(/^#/, "")}`
      : fallback;

  const homeTeamKitColor = sanitizeKitColor(
    data?.home_team?.kit ? `#${data.home_team.kit}` : "#3793D4",
    "#3793D4"
  );
  const awayTeamKitColor = sanitizeKitColor(
    data?.away_team?.kit ? `#${data.away_team.kit}` : "#7CC4EB",
    "#7CC4EB"
  );

  useEffect(() => {
    if (!dataClient) return;

    const container = document.querySelector(".microservice-cont");
    const raw = container?.getAttribute("data-listingdata");
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw)?.data?.data;
      setClientData(parsed || null);
    } catch (err) {
      console.error("Failed to parse data-listingdata:", err);
    }
  }, [dataClient]);

  const homeFormationRaw = data?.home_team?.formation;
  const awayFormationRaw = data?.away_team?.formation;

  const homeFormation = parseFormation(homeFormationRaw || defaultFormation);
  const awayFormation = parseFormation(awayFormationRaw || defaultFormation);

  const filterLineup = (lineup) =>
    lineup?.filter((player) => player.pos !== "gk") || [];

  const homeLineup = filterLineup(data?.home_team?.lineup);
  const awayLineup = filterLineup(data?.away_team?.lineup);

  const buildTeamLineup = (formation, lineup, goalkeaper) => {
    let count = 1;
    const team = [
      goalkeaper?.filter(
        (player) => player.pos === "gk" || player.pos === "Goalkeeper"
      ),
    ];
    for (const playersCount of formation) {
      team.push(lineup.slice(count, count + playersCount));
      count += playersCount;
    }
    return team;
  };

  const homeTeam = buildTeamLineup(
    homeFormation,
    homeLineup,
    data?.home_team?.lineup
  );
  const awayTeam = buildTeamLineup(
    awayFormation,
    awayLineup,
    data?.away_team?.lineup
  ).reverse();

  useEffect(() => {
    if (loadCSS === "true") {
      loadStyles("matchlineup");
    }
  }, [loadCSS]);

  if (!data) {
    return (
      <div className="lineup_box_drop">
        <NoDataFound />
      </div>
    );
  }

  const homeImageUrl = getImageUrl(baseURL, "team", sport, data?.home_team?.id);
  const awayImageUrl = getImageUrl(baseURL, "team", sport, data?.away_team?.id);

  // Check what data is available for conditional rendering
  const hasFormation = data?.home_team?.formation || data?.away_team?.formation;
  const hasLineup = data?.home_team?.lineup?.length > 0 || data?.away_team?.lineup?.length > 0;
  const hasSubstitutions = data?.home_team?.sub?.length > 0 || data?.away_team?.sub?.length > 0;
  const hasBenchPlayers = data?.home_team?.sub_players?.length > 0 || data?.away_team?.sub_players?.length > 0;
  const hasInjuredPlayers = data?.home_team?.inj_susp_players?.length > 0 || data?.away_team?.inj_susp_players?.length > 0;
  const hasCoaches = data?.home_team_coach?.length > 0 || data?.away_team_coach?.length > 0;

  return (
    <>
      {title && <h1>{title}</h1>}
      {((homeTeam || awayTeam) && hasFormation) && (
        <>
          <div className="ft-team-formation-wrapper">
            <div className="ft-team-formation-col">
              <strong>
                <NextImage
                  src={homeImageUrl}
                  height={22}
                  width={22}
                  alt="homeLogo"
                  priority={false}
                />
                <span className="team-name-full">{data?.home_team?.nm}</span>
                <span className="team-name-short">
                  {getShortName(data?.home_team?.nm)}
                </span>
              </strong>
              <span>{data?.home_team?.formation}</span>
            </div>
            <div className="ft-team-formation-col">
              <span>{data?.away_team?.formation}</span>
              <strong>
                <span className="team-name-full">{data?.away_team?.nm}</span>
                <span className="team-name-short">
                  {getShortName(data?.away_team?.nm)}
                </span>
                <NextImage
                  src={awayImageUrl}
                  height={22}
                  width={22}
                  alt="awayLogo"
                  priority={false}
                />
              </strong>
            </div>
          </div>

          <div className="linesup_img">
            <div className="inner_ground">
              <div className="team_one">
                <span className="formation d_md_none">
                  <span className="formation_team">{data?.home_team?.nm}</span>
                  <span className="formation_points">
                    {data?.home_team?.formation}
                  </span>
                </span>
                <LineupRow Team={homeTeam} kitColor={homeTeamKitColor} sport={sport} />
              </div>
              <div className="team_two">
                <span className="formation d_md_none">
                  <span className="formation_team">{data?.away_team?.nm}</span>
                  <span className="formation_points">
                    {data?.away_team?.formation}
                  </span>
                </span>
                <LineupRow Team={awayTeam} kitColor={awayTeamKitColor} sport={sport} />
              </div>
            </div>
          </div>
        </>
      )}

      {hasSubstitutions && (
        <LineupDrop title="Substitutions">
          <FtLineUp home={data?.home_team?.sub} away={data?.away_team?.sub} />
        </LineupDrop>
      )}

      {hasBenchPlayers && (
        <LineupDrop title="Bench Players">
          <SubstitutionTable
            home={data?.home_team?.sub_players}
            away={data?.away_team?.sub_players}
          />
        </LineupDrop>
      )}

      {hasInjuredPlayers && (
        <LineupDrop title="Injured Players">
          <SubstitutionTable
            home={data?.home_team?.inj_susp_players}
            away={data?.away_team?.inj_susp_players}
          />
        </LineupDrop>
      )}

      {hasCoaches && (
        <LineupDrop title="Coaches">
          <LineupBoxFootball
            home={data?.home_team_coach}
            away={data?.away_team_coach}
          />
        </LineupDrop>
      )}
    </>
  );
};

export default Linesup;
